import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './Workshop.css';

interface Work {
  id: number;
  workshop_type: string;
  title: string;
  content: string;
  class_name: string;
  nickname: string;
  created_at: string;
}

const Workshop: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    workshopType: '',
    title: '',
    content: '',
    class_name: '',
    nickname: '',
    file: null as File | null,
  });
  const [works, setWorks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<{ username?: string; class_name?: string } | null>(null);

  useEffect(() => {
    loadUserProfile();
    fetchAllWorks();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setUserProfile(response.data);
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  };

  const fetchAllWorks = async () => {
    try {
      // 获取所有已展示的作品，不按工坊类型筛选
      const response = await api.get('/works');
      setWorks(response.data);
    } catch (error) {
      console.error('获取作品失败:', error);
    }
  };


  const handleFormOpen = (type: string) => {
    setFormData({
      workshopType: type,
      title: '',
      content: '',
      class_name: userProfile?.class_name || '',
      nickname: userProfile?.username || '',
      file: null,
    });
    setShowForm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // 验证音频文件时长（仅限60秒）
      if (formData.workshopType === 'audio') {
        const audio = new Audio();
        audio.src = URL.createObjectURL(file);
        audio.onloadedmetadata = () => {
          if (audio.duration > 60) {
            alert('音频时长不能超过60秒');
            e.target.value = '';
            return;
          }
        };
      }
      
      setFormData({ ...formData, file: file });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('workshopType', formData.workshopType);
      submitData.append('title', formData.title);
      submitData.append('class_name', formData.class_name);
      submitData.append('nickname', formData.nickname);

      if (formData.workshopType === 'text') {
        if (formData.content.length > 500) {
          alert('文字内容不能超过500字');
          setLoading(false);
          return;
        }
        submitData.append('content', formData.content);
      } else if (formData.workshopType === 'video') {
        submitData.append('content', formData.content);
      } else if (formData.file) {
        submitData.append('file', formData.file);
      }

      await api.post('/works', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('作品提交成功！');
      setShowForm(false);
      setFormData({
        workshopType: '',
        title: '',
        content: '',
        class_name: '',
        nickname: '',
        file: null,
      });
      fetchAllWorks();
    } catch (error: any) {
      alert(error.response?.data?.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  const workshops = [
    {
      type: 'audio',
      name: '声工坊',
      icon: '🎤',
      description: '上传革命短故事录音（限60秒）',
    },
    {
      type: 'video',
      name: '影工坊',
      icon: '🎬',
      description: '上传红色小视频（抖音/剪映链接）',
    },
    {
      type: 'image',
      name: '图工坊',
      icon: '🖼️',
      description: '上传红色文化照片或AI生成的红色图片',
    },
    {
      type: 'text',
      name: '文工坊',
      icon: '📝',
      description: '上传美篇、剧本杀、微剧本等文字作品（限500字）',
    },
  ];

  return (
    <div className="workshop-container">
      <h1 className="page-title">红色创客营</h1>

      <div className="workshops-grid">
        {workshops.map((workshop) => (
          <div
            key={workshop.type}
            className="workshop-card"
          >
            <div className="workshop-icon">{workshop.icon}</div>
            <h2>{workshop.name}</h2>
            <p>{workshop.description}</p>
            <button
              className="btn btn-primary"
              onClick={() => handleFormOpen(workshop.type)}
            >
              上传作品
            </button>
          </div>
        ))}
      </div>

      <div className="works-section">
        <h2 className="section-title">作品展示</h2>
        {works.length === 0 ? (
          <div className="empty-works">
            <p>暂无作品展示</p>
          </div>
        ) : (
          <div className="works-bento-grid">
            {works.map((work, index) => {
              // 根据作品类型和索引决定卡片大小
              // 根据作品类型智能分配卡片大小
              let cardClass = 'work-bento-card-small';
              if (work.workshop_type === 'image') {
                // 图片：每5个中的第1个显示为大卡片，其他为中等卡片
                cardClass = index % 5 === 0 ? 'work-bento-card-large' : 'work-bento-card-medium';
              } else if (work.workshop_type === 'text') {
                // 文字：中等卡片，可以显示更多内容
                cardClass = 'work-bento-card-medium';
              } else if (work.workshop_type === 'video') {
                // 视频：根据内容长度决定，长内容用中等卡片
                const contentLength = work.content?.length || 0;
                cardClass = contentLength > 50 ? 'work-bento-card-medium' : 'work-bento-card-small';
              } else {
                // 音频：小卡片
                cardClass = 'work-bento-card-small';
              }

              return (
                <div key={work.id} className={`work-bento-card ${cardClass}`}>
                  <div className="work-bento-header">
                    <span className="work-bento-type">
                      {work.workshop_type === 'audio' && '🎤'}
                      {work.workshop_type === 'video' && '🎬'}
                      {work.workshop_type === 'image' && '🖼️'}
                      {work.workshop_type === 'text' && '📝'}
                    </span>
                    <h3 className="work-bento-title">{work.title}</h3>
                  </div>
                  <div className="work-bento-content">
                    {work.workshop_type === 'image' && work.content.startsWith('/uploads/') && (
                      <img
                        src={work.content}
                        alt={work.title}
                        className="work-bento-image"
                      />
                    )}
                    {work.workshop_type === 'video' && (
                      <div className="work-bento-video">
                        <div className="work-bento-video-content">
                          <p className="work-bento-video-text">{work.content}</p>
                        </div>
                        <div className="work-bento-video-actions">
                          <button
                            className="btn btn-copy-video"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(work.content);
                                alert('已复制到剪贴板！');
                              } catch (error) {
                                // 降级方案
                                const textArea = document.createElement('textarea');
                                textArea.value = work.content;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                                alert('已复制到剪贴板！');
                              }
                            }}
                          >
                            📋 一键复制
                          </button>
                        </div>
                      </div>
                    )}
                    {work.workshop_type === 'audio' && work.content.startsWith('/uploads/') && (
                      <audio
                        src={work.content}
                        controls
                        className="work-bento-audio"
                      />
                    )}
                    {work.workshop_type === 'text' && (
                      <p className="work-bento-text">{work.content}</p>
                    )}
                  </div>
                  <div className="work-bento-meta">
                    <span className="work-bento-author">{work.class_name} · {work.nickname}</span>
                    <span className="work-bento-date">{new Date(work.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowForm(false)}>
              ×
            </button>
            <h2>上传作品</h2>
            <form onSubmit={handleSubmit} className="upload-form">
              <div className="form-group">
                <label>作品名称 *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="input"
                />
              </div>

              {formData.workshopType === 'text' && (
                <div className="form-group">
                  <label>作品内容 *（限500字）</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={8}
                    className="input"
                  />
                  <small>{formData.content.length} / 500</small>
                </div>
              )}

              {formData.workshopType === 'video' && (
                <div className="form-group">
                  <label>视频链接 *</label>
                  <input
                    type="text"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    required
                    className="input"
                    placeholder="粘贴抖音/剪映视频链接或任意内容"
                  />
                </div>
              )}

              {(formData.workshopType === 'audio' || formData.workshopType === 'image') && (
                <div className="form-group">
                  <label>上传文件 *</label>
                  <input
                    type="file"
                    accept={formData.workshopType === 'audio' ? 'audio/*' : 'image/*'}
                    onChange={handleFileChange}
                    required
                    className="input"
                  />
                  {formData.workshopType === 'audio' && (
                    <small>音频时长不能超过60秒</small>
                  )}
                </div>
              )}

              {formData.workshopType === 'image' && (
                <div className="form-group">
                  <label>感受文字</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows={3}
                    className="input"
                    placeholder="写一句感受的文字"
                  />
                </div>
              )}

              <div className="form-group">
                <label>班级 *</label>
                <input
                  type="text"
                  name="class_name"
                  value={formData.class_name}
                  onChange={handleInputChange}
                  required
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>昵称 *</label>
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleInputChange}
                  required
                  className="input"
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? '提交中...' : '提交'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workshop;

